import clsx from "clsx";
import { Fragment, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { LegacyButton, LegacyButtonProps } from "mds/components/LegacyButton";
import {
  ClickEvent,
  Menu,
  MenuDivider,
  MenuItem,
  MenuItemKind,
  MenuProps,
} from "mds/components/Menu";
import { ChevronDownIcon } from "mds/icons";
import { ComponentEventPropertiesWithKey } from "utils/amplitude";

export type DropdownItemType = {
  id: string;
  title: string | ReactNode;
  iconStart?: ReactNode;
  addDivider?: boolean;
};

export type DropdownMenuProps<
  T extends DropdownItemType,
  V extends T[keyof T],
> = ComponentEventPropertiesWithKey & {
  items: T[];
  selectedValue?: V;
  kind?: LegacyButtonProps["kind"];
  size?: LegacyButtonProps["size"];
  className?: string;
  buttonText?: ReactNode;
  onSelectionChange: (selectedItem: T) => void;
  disabled?: boolean;
  truncateWidth?: "xs" | "s" | "m" | "l" | "xl" | "xxl" | `max-w-${string}`;
  allowFullLengthItems?: boolean;
  title?: string;
  menuProps?: Partial<MenuProps>;
  menuItemKind?: MenuItemKind;
  openMenuEventName?: string;
};

export const DropdownMenu = <T extends DropdownItemType, V extends T[keyof T]>({
  selectedValue,
  className,
  menuItemKind,
  kind = "secondary",
  size = "xs",
  truncateWidth = "m",
  allowFullLengthItems = false,
  items,
  buttonText,
  onSelectionChange,
  disabled,
  title,
  eventName,
  eventProperties,
  eventPropertyKey,
  openMenuEventName,
  menuProps,
}: DropdownMenuProps<T, V>) => {
  const truncateWidthClass = truncateWidth
    ? truncateWidth.startsWith("max-w-")
      ? truncateWidth
      : `max-w-${truncateWidth}`
    : "";

  const selectedItem = items.find((item) => item.id === selectedValue) || items[0];

  const onItemClick = (e: ClickEvent) => {
    const item = e.value as T;
    onSelectionChange(item);
  };

  const menuButton = (
    <LegacyButton
      className={twMerge(className, "gap-1", truncateWidthClass)}
      disabled={disabled}
      eventName={openMenuEventName}
      kind={kind}
      size={size}
      title={title}
    >
      <div className={clsx("flex items-center gap-1 truncate", truncateWidthClass)}>
        {!buttonText && selectedItem?.iconStart}
        <span className="min-w-0 truncate">{buttonText || selectedItem?.title}</span>
      </div>
      <ChevronDownIcon />
    </LegacyButton>
  );

  const MenuItems = items.map((item) => {
    const selected = item.id === selectedValue;
    return (
      <Fragment key={item.id}>
        <MenuItem
          checked={selected}
          className={clsx({ selected })}
          disabled={"disabled" in item ? (item.disabled as boolean) : false}
          eventName={eventName}
          eventProps={{
            ...eventProperties,
            [eventPropertyKey || "itemId"]: item.id,
          }}
          kind={menuItemKind}
          title={typeof item.title === "string" ? item.title : undefined}
          value={item}
          onClick={onItemClick}
        >
          {item.iconStart}
          <span
            className={clsx(
              truncateWidth && !allowFullLengthItems && `block truncate ${truncateWidthClass}`,
            )}
          >
            {item.title}
          </span>
        </MenuItem>
        {item.addDivider && <MenuDivider />}
      </Fragment>
    );
  });

  return (
    <Menu menuButton={menuButton} overflow="auto" {...menuProps}>
      {MenuItems}
    </Menu>
  );
};
